import type { Client } from "@xmpp/client"
import AdHocCommands from "@/api/adHocCommands"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import {
  MessageDisplayType,
  type DocumentDriverContext,
} from "@/reducers/messagesSlice"
import type { ImageMetadataOverride } from "@/reducers/overlaysSlice"

type WizardDriverContext = {
  messageId?: string
  plates?: string
  location?: { formattedAddress?: string; [key: string]: unknown }
  [key: string]: unknown
}

type WizardExtractedMetadata = {
  data?: Record<string, string>
  documentType?: string
  blurry?: boolean
  remarks?: string
  [key: string]: unknown
}

type WizardDocumentInfo = {
  messageId?: string
  driverContext?: WizardDriverContext | null
  extractedMetadata?: WizardExtractedMetadata | null
  documentLink?: Record<string, unknown> | null
  forwarded?: {
    fileUrl?: string
    fileName?: string
    author?: string
    chatJid?: string
    fromJid?: string
    timestamp?: string
  } | null
}

function toDocumentDriverContext(
  ctx: WizardDriverContext | null | undefined,
): DocumentDriverContext | undefined {
  if (!ctx) return undefined
  return {
    id: "",
    to: "",
    from: "",
    messageId: ctx.messageId ?? "",
    type: "groupchat",
    timestamp: "",
    plates: ctx.plates ?? "",
    location: ctx.location as DocumentDriverContext["location"],
    displayType: MessageDisplayType.DocumentDriverContext,
  }
}

function toImageMetadataOverride(
  info: WizardDocumentInfo,
): ImageMetadataOverride {
  return {
    extractedMetadata: info.extractedMetadata?.data,
    driverContext: toDocumentDriverContext(info.driverContext),
    documentType: info.extractedMetadata?.documentType,
    blurry: info.extractedMetadata?.blurry,
    remarks: info.extractedMetadata?.remarks,
  }
}

async function fetchEnvelope(envelopeJson: string) {
  const envelope = JSON.parse(envelopeJson)
  if (envelope && typeof envelope.url === "string") {
    const res = await fetch(envelope.url)
    if (!res.ok) throw new Error(`JSON envelope fetch failed: ${res.status}`)
    return await res.json()
  }
  return envelope
}

/**
 * Module-level cache keyed by the sorted URL set. Keeps responses around
 * for the lifetime of the tab so reopening the same gallery is instant.
 */
const cache = new Map<string, Record<string, ImageMetadataOverride>>()
const inFlight = new Map<
  string,
  Promise<Record<string, ImageMetadataOverride>>
>()

function cacheKey(fileUrls: string[]): string {
  return [...fileUrls].sort().join("|")
}

/**
 * Fetch (or return cached) image metadata for the given file URLs.
 * Deduplicates concurrent requests for the same URL set.
 * Returned object is keyed by the MAPPED (same-origin) src URL.
 */
export async function fetchAIChatImageMetadata(
  client: Client,
  fileUrls: string[],
): Promise<Record<string, ImageMetadataOverride>> {
  if (fileUrls.length === 0) return {}
  const key = cacheKey(fileUrls)
  const cached = cache.get(key)
  if (cached) return cached
  const existing = inFlight.get(key)
  if (existing) return existing

  const promise = (async () => {
    const iq = await AdHocCommands.getDocumentsByFileUrls(client, fileUrls)
    const jsonEl = iq
      .getChild("command", "http://jabber.org/protocol/commands")
      ?.getChild("json", "urn:xmpp:json:0")
    if (!jsonEl) throw new Error("No JSON payload in response")
    const body = await fetchEnvelope(jsonEl.text())
    const documents =
      (body?.documents as Record<string, WizardDocumentInfo | null>) || {}

    const overrides: Record<string, ImageMetadataOverride> = {}
    for (const [rawUrl, info] of Object.entries(documents)) {
      if (!info) continue
      overrides[mapFileUrlToSameOrigin(rawUrl)] = toImageMetadataOverride(info)
    }
    cache.set(key, overrides)
    return overrides
  })()

  inFlight.set(key, promise)
  try {
    return await promise
  } finally {
    inFlight.delete(key)
  }
}
